import { Injectable, Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router } from '@angular/router';
import { ManageSession } from '../services/manage-session';
import { AuthEndPoints } from './apiconstant';
import { Commonservice } from '../services/commonservice';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private pendingRequests = 0;
  private redirectingToSignin = false;

  constructor(
    private readonly spinner: NgxSpinnerService,
    private readonly sessionSvc: ManageSession,
    private readonly router: Router,
    private readonly injector: Injector,
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    this.startLoader();

    const token = this.sessionSvc.getAuthToken();
    const authReq = token
      ? req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
          },
        })
      : req;

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.shouldHandleUnauthorized(error, req)) {
          this.handleUnauthorizedSession();
          return EMPTY;
        }

        return throwError(() => error);
      }),
      finalize(() => {
        this.stopLoader();
      }),
    );
  }

  private shouldHandleUnauthorized(error: HttpErrorResponse, req: HttpRequest<unknown>): boolean {
    return error.status === 401 && !this.isPublicAuthRequest(req.url);
  }

  private isPublicAuthRequest(url: string): boolean {
    return [
      AuthEndPoints.LOGIN,
      AuthEndPoints.CAPTCHA,
      AuthEndPoints.FORGOT_PASSWORD_MAIL,
      AuthEndPoints.VERIFY_FORGOT_PASSWORD_LINK,
    ].some((endpoint) => typeof endpoint === 'string' && url.includes(endpoint));
  }

  private handleUnauthorizedSession(): void {
    this.injector.get(Commonservice).clearSession();

    if (this.redirectingToSignin) {
      return;
    }

    this.redirectingToSignin = true;
    this.router.navigate(['/signin']).finally(() => {
      this.redirectingToSignin = false;
    });
  }

  private startLoader(): void {
    this.pendingRequests += 1;
    if (this.pendingRequests === 1) {
      this.spinner.show();
    }
  }

  private stopLoader(): void {
    this.pendingRequests = Math.max(this.pendingRequests - 1, 0);
    if (this.pendingRequests === 0) {
      this.spinner.hide();
    }
  }
}
